import { useEffect, useMemo } from 'react';
import { IProjectDocumentInfoItem } from '../redux/project/documents/sliceProjectDocuments';
import {
    getStructureItemInfo,
    IStructureItemInfo,
    mapCheckedCategoriesToRequest,
} from '../util/StructureUtils';
import { Area, StructureViewMode } from '../types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import store, { IAppReduxStore } from '../redux/store';
import { categorizeImportDocuments } from '../util/ImportUtils';
import {
    getProjectCategorizationStructure,
    updateProjectCategorizationStructure,
} from '../redux/general/sliceCategorization';
import { fetchViewData } from '../util/ProjectUtils';

export const useCategorizationMapper = ({
    replicaId,
    onClose,
    area,
}: {
    replicaId: string;
    onClose: () => void;
    area: Area;
}): {
    structureItems: IStructureItemInfo[];
    selectedDocIds: string[];
    selectedDocuments: IProjectDocumentInfoItem[];
    onConfirm: (fullChecked: string[], halfChecked: string[]) => Promise<void>;
    mode: StructureViewMode;
    loading: boolean;
} => {
    const params = useParams();

    const {
        mode,
        structure: { data, loading },
    } = useSelector((_store: IAppReduxStore) => _store.categorization);

    const importDocs = useSelector((_store: IAppReduxStore) => _store.importDocuments.data);
    const projectDocs = useSelector((_store: IAppReduxStore) => _store.projectDocuments.info.data);

    const structureItems = useMemo(
        () => data.map(item => getStructureItemInfo(data, item)),
        [data],
    );
    const selectedDocIds = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds,
    );

    const selectedDocuments = useMemo((): IProjectDocumentInfoItem[] => {
        if (area === 'import') {
            return (importDocs ?? [])
                .filter(doc => selectedDocIds.includes(doc.UniversalID))
                .map(x => x as unknown as IProjectDocumentInfoItem);
        }
        return projectDocs;
    }, [importDocs, projectDocs, selectedDocIds]);

    const onConfirm = async (fullChecked: string[], halfChecked: string[]): Promise<void> => {
        if (area === 'import') {
            categorizeImportDocuments(
                replicaId,
                selectedDocuments.map(x => x.UniversalID),
                data
                    .filter(x => fullChecked.includes(x.IdDocIndex))
                    .map(x => ({ Id: x.IdDocIndex, FullPath: x.Subject })),
                'replace',
                mode,
                selectedDocuments.length,
            );
        } else {
            await store.dispatch(
                updateProjectCategorizationStructure({
                    replicaId,
                    payload: mapCheckedCategoriesToRequest(
                        selectedDocuments,
                        data,
                        mode,
                        fullChecked,
                        halfChecked,
                    ),
                    count: selectedDocuments.length,
                }),
            );
            fetchViewData(params);
        }
        onClose();
    };

    useEffect(() => {
        if (!mode) {
            return;
        }
        store.dispatch(getProjectCategorizationStructure({ replicaId, structureName: mode }));
    }, [mode]);

    return {
        structureItems,
        onConfirm,
        selectedDocIds,
        selectedDocuments,
        mode,
        loading,
    };
};
